import { useCallback, useState } from "react";
import { stockAvailabilityService } from "@/features/stocks/services/stock-availability.service";
import type { StockAvailabilityResponseDTO } from "@/features/stocks/types/stock.types";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import { toAvailabilityDate } from "../utils/stock-availability.utils";

interface UseStockAvailabilityOptions {
  /** ISO datetime — convertido para `yyyy-MM-dd` antes de enviar */
  startDate: string;
  /** ISO datetime — convertido para `yyyy-MM-dd` antes de enviar */
  endDate: string;
}

interface CheckResult {
  data: StockAvailabilityResponseDTO | null;
  error: string | null;
}

/**
 * Hook utilitário para chamar `GET /stocks/availability` por barcode.
 *
 * `startDate` e `endDate` aceitam ISO datetime (formato dos campos
 * `expectedOutAt`/`expectedInAt`) — o hook trunca para `yyyy-MM-dd`
 * antes de chamar o endpoint.
 */
export function useStockAvailability({
  startDate,
  endDate,
}: UseStockAvailabilityOptions) {
  const [checking, setChecking] = useState(false);

  const check = useCallback(
    async (barcode: string): Promise<CheckResult> => {
      if (!startDate || !endDate) {
        return {
          data: null,
          error: "Defina as datas de retirada e devolução antes",
        };
      }
      setChecking(true);
      try {
        const response = await stockAvailabilityService.getAvailability({
          barcodes: [barcode],
          startDate: toAvailabilityDate(startDate),
          endDate: toAvailabilityDate(endDate),
        });
        const item = response[0];
        if (!item) {
          return { data: null, error: "Peça não encontrada" };
        }
        return { data: item, error: null };
      } catch (error: unknown) {
        return {
          data: null,
          error: getApiErrorMessage(error, "Erro ao consultar disponibilidade"),
        };
      } finally {
        setChecking(false);
      }
    },
    [startDate, endDate],
  );

  return { check, checking };
}
